import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch } from 'redux/store/store.types';
import { ApiPaginationResponse } from 'types';
import { getApiErrorMessage } from 'utils';

import {
  useLazyFetchGoCertificatesQuery,
  useLazyFetchShortenGoCertificatesQuery,
  useUpdateGoCertificateMutation,
} from '../api/eecs-certificates.api';
import { GoCertificateAC, setCertificateModal, setSelectedCertificate } from '../store/eecs-certificates.slice';
import {
  FetchGoCertificatePayload,
  FetchGoCertificatesShortenPayload,
  IEecsCertificateModal,
  IGoCertificate,
  IGoCertificateShorten,
  UpdateGoCertificatePayload,
} from '../types/eecs-certificates.type';

interface OpenModal {
  modal: IEecsCertificateModal;
  certificate: IGoCertificate | null;
}

interface UseEecsGoCertificate {
  fetchGoCertificatesHandler: (payload: FetchGoCertificatePayload) => Promise<boolean>;
  setGoCertificateCommentHandler: (payload: UpdateGoCertificatePayload) => Promise<boolean>;
  fetchShortenGoCertificatesHandler: (
    payload: FetchGoCertificatesShortenPayload,
  ) => Promise<ApiPaginationResponse<IGoCertificateShorten[]> | null>;
  openCertificateModal: (payload: OpenModal) => void;
  closeCertificateModal: () => void;
  clearCertificateState: () => void;
}

export const useEecsGoCertificate = (): UseEecsGoCertificate => {
  const dispatch = useAppDispatch();

  const [fetchGoCertificates] = useLazyFetchGoCertificatesQuery();
  const [fetchShortenGoCertificates] = useLazyFetchShortenGoCertificatesQuery();
  const [updateNecsGoCertificate] = useUpdateGoCertificateMutation();

  const fetchShortenGoCertificatesHandler = async (payload: FetchGoCertificatesShortenPayload) => {
    const { data: response, error } = await fetchShortenGoCertificates(payload);
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Certificates!');
      return null;
    }
    return response;
  };

  const fetchGoCertificatesHandler = async (payload: FetchGoCertificatePayload) => {
    dispatch(GoCertificateAC.toggleLoading(true));
    const { data: response, error } = await fetchGoCertificates(payload);

    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get EECS Certificates!');
    } else {
      batch(() => {
        dispatch(GoCertificateAC.setItems(response.data));
        dispatch(GoCertificateAC.setPaginationMeta(response.meta));
      });
    }

    dispatch(GoCertificateAC.toggleLoading(false));
    return !isError;
  };

  const setGoCertificateCommentHandler = async (payload: UpdateGoCertificatePayload) => {
    dispatch(GoCertificateAC.toggleLoading(true));
    const { data: response, error } = await updateNecsGoCertificate(payload);
    dispatch(GoCertificateAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to change comment for Certificate Bundle!');
      return false;
    }
    toast.success('Comment for Certificate Bundle successfully saved!');
    return true;
  };

  const openCertificateModal = ({ modal, certificate }: OpenModal) => {
    batch(() => {
      dispatch(setSelectedCertificate(certificate));
      dispatch(setCertificateModal(modal));
    });
  };

  const closeCertificateModal = () => {
    dispatch(setCertificateModal(null));
    dispatch(setSelectedCertificate(null));
  };

  const clearCertificateState = () => {
    batch(() => {
      dispatch(GoCertificateAC.clearItems());
      dispatch(GoCertificateAC.clearFilters());
      dispatch(GoCertificateAC.clearSorts());
      dispatch(GoCertificateAC.setCurrentPage(1));
      dispatch(setCertificateModal(null));
      dispatch(setSelectedCertificate(null));
    });
  };

  return {
    fetchShortenGoCertificatesHandler,
    fetchGoCertificatesHandler,
    setGoCertificateCommentHandler,
    closeCertificateModal,
    openCertificateModal,
    clearCertificateState,
  };
};
